import * as React from 'react';
import { SCOPE_BRIEF, workTypeLabel, type PriorityLevel, type ScopeMeta } from '@protoss/shared';
import { Pill, SkeletonBlock, type PillVariant } from '@protoss/ui';

import '../../../pages/deal-view-page.css';
import {
  type SalesDealQuestionnaireSummary,
  type SalesDealSummary,
} from '../services/salesApi';
import {
  fieldHasVisibleContent,
  hasScopeBriefContent,
  resolveFieldDisplayValue,
  type FieldDisplayValue,
} from '../utils/field-display-value';
import {
  isFieldVisibleByRules,
  type QuestionnaireAnswerMap,
  type QuestionnaireFieldLookup,
} from '../utils/questionnaire-field-visibility';
import { BprDealArchitecture } from '../../bpr/components/BprDealArchitecture';
import { BprSectionDiagram } from '../../bpr/components/BprSectionDiagram';
import { scopeStatusChip } from '../utils/scope-doc';
import { useScrollToFocusedAdhoc } from '../utils/useScrollToFocusedAdhoc';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PRIORITY_VARIANT: Record<PriorityLevel, PillVariant> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
};
const PRIORITY_LABEL: Record<PriorityLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

/**
 * Deal status pill. `noScopeCaptured` (COS-100, docs/plans/72 - the same signal that greys out
 * Generate): a questionnaire can be complete while no scope item is captured yet, and "Ready for
 * Generation" then sends the user to a disabled button - say what to do instead. Display only:
 * the persisted status is unchanged.
 */
export function getDealStatusPresentation(
  status: SalesDealSummary['status'] | null | undefined,
  opts?: { noScopeCaptured?: boolean }
): {
  label: string;
  variant: PillVariant;
} {
  if (typeof status !== 'string' || !status.trim()) {
    return { label: 'Unknown', variant: 'neutral' };
  }
  switch (status) {
    case 'draft':
      return { label: 'Draft', variant: 'muted' };
    case 'ready_for_generation':
      return opts?.noScopeCaptured
        ? { label: 'Add scope first', variant: 'warning' }
        : { label: 'Ready for Generation', variant: 'warning' };
    case 'generated':
      return { label: 'Generated', variant: 'success' };
    default:
      return { label: status.replace(/_/g, ' '), variant: 'neutral' };
  }
}

// ─── Field display renderer ───────────────────────────────────────────────────

function FieldValue({ dv }: { dv: FieldDisplayValue }) {
  if (dv.kind === 'empty') {
    return <span className="deal-view-page__field-empty">-</span>;
  }
  if (dv.kind === 'text') {
    return <span className="deal-view-page__field-text">{dv.text}</span>;
  }
  if (dv.kind === 'pills') {
    return (
      <div className="deal-view-page__field-pills">
        {dv.pills.map((label) => (
          <Pill key={label} variant="neutral">
            {label}
          </Pill>
        ))}
      </div>
    );
  }
  // table
  if (dv.columns.length === 0) return <span className="deal-view-page__field-empty">-</span>;
  return (
    <div className="deal-view-page__field-table-wrap">
      <table className="deal-view-page__field-table">
        <thead>
          <tr>
            {dv.columns.map((col) => (
              <th key={col.key} className="deal-view-page__field-table-th">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dv.rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i}>
              {dv.columns.map((col) => (
                <td key={col.key} className="deal-view-page__field-table-td">
                  {row[col.key] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Scope brief (read-only) ──────────────────────────────────────────────────

/**
 * Read-only render of an ad-hoc item's scope brief (status / method / problem / outcome /
 * in-out scope / drivers / assumptions / notes). The text/tag dimensions are driven by the shared
 * SCOPE_BRIEF descriptor so it stays in sync with the wizard + extractor.
 */
function ScopeBriefView({ scope }: { scope: ScopeMeta }) {
  // The requested/context status pill is rendered next to the field label (always shown for an
  // ad-hoc primary), so here we only surface the work method.
  return (
    <div className="deal-view-page__scope-brief">
      {scope.workType ? (
        <div className="deal-view-page__field-pills">
          <Pill variant="neutral">{workTypeLabel(scope.workType)}</Pill>
        </div>
      ) : null}
      {SCOPE_BRIEF.map((d) => {
        const v = scope[d.key];
        if (d.control === 'text') {
          const t = typeof v === 'string' ? v.trim() : '';
          if (!t) return null;
          return (
            <div key={d.key} className="deal-view-page__scope-row">
              <span className="deal-view-page__scope-label">{d.label}</span>
              <span className="deal-view-page__field-text">{t}</span>
            </div>
          );
        }
        const items = Array.isArray(v) ? v : [];
        if (items.length === 0) return null;
        return (
          <div key={d.key} className="deal-view-page__scope-row">
            <span className="deal-view-page__scope-label">{d.label}</span>
            <div className="deal-view-page__field-pills">
              {items.map((it) => (
                <Pill key={it} variant="neutral">
                  {it}
                </Pill>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── View surface ─────────────────────────────────────────────────────────────

export interface DealViewSurfaceProps {
  loading: boolean;
  questionnaire: SalesDealQuestionnaireSummary | null;
  answers: QuestionnaireAnswerMap;
  fieldLookup: QuestionnaireFieldLookup;
  showAnsweredOnly: boolean;
  compactMode?: boolean;
  selectedSectionKeys?: string[];
  /** Split-view cross-pane focus: the ad-hoc fieldKey to scroll to + highlight (null = none). */
  focusedAdhocFieldKey?: string | null;
  /** When set (Prioritization open in the split), clicking an AI-captured question focuses it. */
  onFocusAdhocField?: (fieldKey: string) => void;
  /** BPR deals: non-viewers get the Generate/Regenerate diagram controls. */
  canGenerateBprDiagrams?: boolean;
}

export function DealViewSurface({
  loading,
  questionnaire,
  answers,
  fieldLookup,
  showAnsweredOnly,
  compactMode,
  selectedSectionKeys: providedSelectedSectionKeys,
  focusedAdhocFieldKey = null,
  onFocusAdhocField,
  canGenerateBprDiagrams = false,
}: DealViewSurfaceProps) {
  // Cross-pane focus: scroll + flash the matching ad-hoc field when focus changes (split view).
  const rootRef = React.useRef<HTMLDivElement>(null);
  useScrollToFocusedAdhoc(focusedAdhocFieldKey, rootRef);

  if (loading) {
    return (
      <div className="deal-view-page__sections">
        {[0, 1, 2].map((i) => (
          <div key={i} className="deal-view-page__section">
            <SkeletonBlock height="20px" width="30%" />
            <div className="deal-view-page__fields-grid">
              {[0, 1, 2, 3, 4, 5].map((j) => (
                <div key={j} className="deal-view-page__field">
                  <SkeletonBlock height="12px" width="55%" />
                  <SkeletonBlock height="16px" width="75%" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!questionnaire) return null;

  const rules = questionnaire.conditionalRules;

  // Use provided selectedSectionKeys from parent (in split compact mode) or all sections (normal mode)
  const visibleSections =
    compactMode && providedSelectedSectionKeys
      ? questionnaire.sections.filter((s) => providedSelectedSectionKeys.includes(s.sectionKey))
      : questionnaire.sections;

  // BPR deals: the System Architecture section renders right AFTER the
  // company section (the one holding primary_contact), BEFORE the dynamic
  // business-process sections - per the BPR flow's section order. When the
  // company section is absent/filtered out it pins to the very top instead.
  // Renders nothing for non-BPR deals (no linked engagement).
  const archAnchorIdx = visibleSections.findIndex((s) =>
    s.groups.some((g) => g.groupKey === 'primary_contact')
  );
  const architectureNode = (
    <BprDealArchitecture
      dealUuid={questionnaire.dealUuid}
      canGenerate={canGenerateBprDiagrams}
    />
  );

  return (
    <div
      ref={rootRef}
      className={`deal-view-page__content${compactMode ? ' deal-view-page__content--compact' : ''}`}
    >
      <div className="deal-view-page__sections">
        {archAnchorIdx < 0 ? architectureNode : null}
        {visibleSections.map((section, sectionIdx) => {
          const architectureAfter = sectionIdx === archAnchorIdx ? architectureNode : null;
          // Check if any field is visible (and answered, when filtered)
          const hasVisibleContent = section.groups.some((group) =>
            group.fields.some((field) => {
              if (!isFieldVisibleByRules(field.fieldKey, rules, answers, fieldLookup)) return false;
              if (showAnsweredOnly) {
                return fieldHasVisibleContent(field, answers);
              }
              return true;
            })
          );

          if (!hasVisibleContent) {
            return architectureAfter ? (
              <React.Fragment key={section.sectionKey}>{architectureAfter}</React.Fragment>
            ) : null;
          }

          // Two-level for the ad-hoc scope section: cluster the per-question groups by their coarse
          // Business-Process (the primary field's scopeMeta.businessProcess) under a sub-header.
          const isAdhocSection = section.groups.some((g) => g.fields.some((f) => f.isAdhocPrimary));
          const bpOf = (group: (typeof section.groups)[number]): string =>
            group.fields.find((f) => f.isAdhocPrimary)?.scopeMeta?.businessProcess?.trim() || '';
          const orderedGroups = isAdhocSection
            ? [...section.groups].sort((a, b) => bpOf(a).localeCompare(bpOf(b)))
            : section.groups;
          const showBpHeaders =
            isAdhocSection && new Set(orderedGroups.map(bpOf).filter(Boolean)).size > 1;

          return (
            <React.Fragment key={section.sectionKey}>
            <div
              className="deal-view-page__section"
              data-section-key={section.sectionKey}
            >
              <h2 className="deal-view-page__section-title">{section.label}</h2>

              {/* BPR deals: the section OPENS with its current-state process
                  flow (same cached spec as the engagement's Diagrams tab);
                  renders nothing for non-BPR deals or undiagrammed sections. */}
              <BprSectionDiagram
                dealUuid={questionnaire.dealUuid}
                sectionKey={section.sectionKey}
                state="current"
              />

              {orderedGroups.map((group, gi) => {
                const visibleFields = group.fields.filter((field) => {
                  if (!isFieldVisibleByRules(field.fieldKey, rules, answers, fieldLookup)) {
                    return false;
                  }
                  if (showAnsweredOnly) {
                    return fieldHasVisibleContent(field, answers);
                  }
                  return true;
                });

                if (visibleFields.length === 0) return null;

                const bp = bpOf(group);
                const prevBp = gi > 0 ? bpOf(orderedGroups[gi - 1]!) : null;
                const emitBpHeader = showBpHeaders && !!bp && bp !== prevBp;

                return (
                  <React.Fragment key={group.groupKey}>
                    {emitBpHeader ? <h3 className="deal-view-page__bp-header">{bp}</h3> : null}
                    <div className="deal-view-page__group" data-group-key={group.groupKey}>
                      {group.label ? (
                        <h3 className="deal-view-page__group-label">{group.label}</h3>
                      ) : null}
                    <div className="deal-view-page__fields-grid">
                      {visibleFields.map((field) => {
                        const dv = resolveFieldDisplayValue(field, answers);
                        // Only the PRIMARY field of an ad-hoc group is the scope item ("What was
                        // discussed" + brief); supplementary fields keep their own label + value.
                        const showBrief =
                          !!field.isAdhocPrimary && hasScopeBriefContent(field.scopeMeta);
                        // Clicking an AI-captured scope item focuses it across the split view.
                        const adhocFocusable = !!field.isAdhocPrimary && !!onFocusAdhocField;
                        // One canonical status pill (label + colour) shared with the Scope tab.
                        const statusChip = scopeStatusChip(field.scopeMeta ?? undefined);
                        return (
                          <div
                            key={field.fieldKey}
                            data-adhoc-fieldkey={field.isAdhocPrimary ? field.fieldKey : undefined}
                            className={`deal-view-page__field${dv.kind === 'table' || showBrief ? ' deal-view-page__field--full' : ''}${adhocFocusable ? ' deal-view-page__field--adhoc-focusable' : ''}`}
                            onClick={
                              adhocFocusable ? () => onFocusAdhocField?.(field.fieldKey) : undefined
                            }
                          >
                            {field.isAdhocPrimary ? (
                              <div className="deal-view-page__field-label-row">
                                <span className="deal-view-page__field-label">
                                  What was discussed
                                </span>
                                <Pill variant={statusChip.variant}>{statusChip.label}</Pill>
                                {field.scopeMeta?.customerPriority ? (
                                  <Pill variant={PRIORITY_VARIANT[field.scopeMeta.customerPriority]}>
                                    Customer: {PRIORITY_LABEL[field.scopeMeta.customerPriority]}
                                  </Pill>
                                ) : null}
                                {field.scopeMeta?.recommendedPriority ? (
                                  <Pill
                                    variant={PRIORITY_VARIANT[field.scopeMeta.recommendedPriority]}
                                  >
                                    Rec: {PRIORITY_LABEL[field.scopeMeta.recommendedPriority]}
                                  </Pill>
                                ) : null}
                              </div>
                            ) : (
                              <span className="deal-view-page__field-label">{field.label}</span>
                            )}
                            <div className="deal-view-page__field-value">
                              <FieldValue dv={dv} />
                            </div>
                            {showBrief && field.scopeMeta ? (
                              <ScopeBriefView scope={field.scopeMeta} />
                            ) : null}
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {/* BPR deals: the section CLOSES with its future-state process
                  flow (after the last question, before the next section). */}
              <BprSectionDiagram
                dealUuid={questionnaire.dealUuid}
                sectionKey={section.sectionKey}
                state="future"
              />
            </div>
            {architectureAfter}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
